// Copyright (c) Zammo Inc.
import React, { useMemo } from 'react';
import { ZammoWebChat } from '@bfc/zammo/lib/ui-components/ZammoWebChat';

import {
  getPersonalizerConfiguration,
  getSimulatorSession,
  getSimulatorSpeechSession,
  SimulatorSession,
  uploadFilesForSimulator,
} from '../../app/actions';

interface Props {
  /**
   * @property {string} session simulator session containing details needed to connect to Zammo business.
   */
  session: SimulatorSession;

  /**
   * @property {string} simulatorToken the token generated by the zammo API to authenticate the business with Zammo business.
   */
  simulatorToken: string;
}

const Simulator = ({ simulatorToken, session }: Props) => {
  // Check for useV2 query parameter
  const useV2 = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('useV2') === 'true';
  }, []);

  // Replace URLs with V2 versions if useV2 is enabled
  const scriptSrcUrl = useMemo(() => {
    if (!useV2 || !session.uiKitScriptUrl) return session.uiKitScriptUrl as string;

    // Replace V1 script URL with V2 version
    // Pattern: zammo-bot-ui-kit.min.js -> zammo-bot-ui-kit-v2.umd.js
    return session.uiKitScriptUrl.replace(/zammo-bot-ui-kit[^/]*\.js/, 'zammo-bot-ui-kit-v2-latest.umd.js');
  }, [useV2, session.uiKitScriptUrl]);

  const scriptCssUrl = useMemo(() => {
    if (!useV2 || !session.uiKitStyleUrl) return session.uiKitStyleUrl as string;

    // Replace V1 CSS URL with V2 version
    // Pattern: zammo-bot-ui-kit.min.css -> zammo-bot-ui-kit-v2.css
    return session.uiKitStyleUrl.replace(/zammo-bot-ui-kit[^/]*\.css/, 'zammo-bot-ui-kit-v2-latest.css');
  }, [useV2, session.uiKitStyleUrl]);
  const getDirectlineToken = async (userId: string) => {
    const data = await getSimulatorSession(simulatorToken, userId);

    return data.directLineToken;
  };

  const getSpeechSessionInfo = async () => {
    const speechSession = await getSimulatorSpeechSession(simulatorToken);

    return { token: speechSession.speechToken, region: speechSession.speechRegion };
  };

  const uploadFiles = async (files: File[], conversationId: string, expiryMinutes: number) => {
    return uploadFilesForSimulator(simulatorToken, files, conversationId, expiryMinutes);
  };

  const getSimulatorToken = async () => {
    return simulatorToken;
  };

  const getExtensionContent = async () => ({
    js: session.customJsContent as string,
    css: session.customCssContent as string,
  });

  return (
    <ZammoWebChat
      useV2={useV2}
      getDirectlineToken={getDirectlineToken}
      getSpeechSessionInfo={getSpeechSessionInfo}
      scriptCssUrl={scriptCssUrl}
      scriptSrcUrl={scriptSrcUrl}
      zammoApiBaseUrl={window.ZAMMO_API_URL}
      isStandAlone={true}
      isTryZammoWebchat={false}
      getSimulatorToken={getSimulatorToken}
      getExtensionContent={getExtensionContent}
      getPersonalizerConfiguration={(locale?: string) => getPersonalizerConfiguration(simulatorToken, locale)}
      uploadFiles={uploadFiles}
    />
  );
};

export default Simulator;
